import "core-js/modules/es.symbol";
import "core-js/modules/es.symbol.description";
import "core-js/modules/es.array.index-of";
import "core-js/modules/es.object.assign";
import "core-js/modules/es.object.keys";
import _noop from "lodash/noop";
import _uniqueId from "lodash/uniqueId";

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import * as React from 'react';
import classNames from 'classnames';
import { AlignTarget } from "../Align";
import { PressableContext, PressableCore, PressableControlLabel } from "../Pressable";
import FocusHandler from "../handler/FocusHandler";
import HoverHandler from "../handler/HoverHandler";

var getPressableColor = function getPressableColor(color) {
  if (color === 'blue') {
    return 'primary';
  } else if (color === 'orange') {
    return 'warning';
  }

  return color;
};

var useCallback = React.useCallback,
    useRef = React.useRef;

var Switch = function Switch(props) {
  var generatedID = useRef(_uniqueId('checkbox'));
  var handleChange = useCallback(function (e) {
    if (props.disabled) {
      return;
    }

    var target = e.target;
    props.onChange(target.checked);
  }, [props]);

  var getCursor = function getCursor() {
    var disabled = props.disabled;
    return disabled ? 'default' : 'pointer';
  };

  var getFill = function getFill(isChecked) {
    var color = props.color;
    return isChecked ? getPressableColor(color) : 'inset';
  };

  var getHeight = function getHeight() {
    var size = props.size;
    return size === 'small' ? 14 : 18;
  };

  var getKeyline = function getKeyline(isChecked) {
    var color = props.color;
    var keylineColor = isChecked ? getPressableColor(color) : 'base';
    return {
      color: keylineColor,
      side: 'all',
      width: 1
    };
  };

  var getShadow = function getShadow(isFocused) {
    var disabled = props.disabled;
    return {
      focused: !disabled && isFocused
    };
  };

  var getWidth = function getWidth() {
    var size = props.size;
    return size === 'small' ? 24 : 34;
  };

  var getKnobDimensions = function getKnobDimensions() {
    var size = props.size;
    return size === 'small' ? 14 : 18;
  };

  var getKnobFill = function getKnobFill() {
    var disabled = props.disabled;
    return disabled ? 'inset' : 'base';
  };

  var getKnobShadow = function getKnobShadow(isHovering) {
    var disabled = props.disabled;
    return {
      base: !disabled,
      elevated: !disabled,
      hovered: !disabled && isHovering
    };
  };

  var renderSwitch = function renderSwitch(focusProps, hoverProps, idValue, isFocused, isHovering) {
    var ariaDescribedBy = props['aria-describedby'],
        ariaLabel = props['aria-label'],
        ariaLabelledBy = props['aria-labelledby'],
        color = props.color,
        description = props.description,
        disabled = props.disabled,
        id = props.id,
        invalid = props.invalid,
        label = props.label,
        value = props.value,
        wrap = props.wrap,
        remaining = _objectWithoutProperties(props, ["aria-describedby", "aria-label", "aria-labelledby", "color", "description", "disabled", "id", "invalid", "label", "value", "wrap"]);

    var isChecked = value;
    return /*#__PURE__*/React.createElement(AlignTarget, {
      lineHeight: getHeight(),
      nestable: true,
      strategy: "center"
    }, function (alignProps) {
      return /*#__PURE__*/React.createElement(PressableCore, _extends({}, alignProps, focusProps, hoverProps, {
        className: "Switch-switch",
        cursor: getCursor(),
        fill: getFill(isChecked),
        height: getHeight(),
        keyline: getKeyline(isChecked),
        overlay: disabled,
        radius: getHeight(),
        shadow: getShadow(isFocused),
        width: getWidth()
      }), /*#__PURE__*/React.createElement(PressableContext.Consumer, null, function (context) {
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", _extends({}, remaining, {
          "aria-describedby": ariaDescribedBy,
          "aria-invalid": invalid && 'true',
          "aria-label": ariaLabel,
          "aria-labelledby": ariaLabelledBy,
          className: classNames('Switch-source', context.pressableContextClasses),
          checked: value,
          disabled: disabled,
          id: idValue,
          onChange: handleChange,
          type: "checkbox"
        })), /*#__PURE__*/React.createElement(PressableCore, {
          className: "Switch-knob",
          cursor: getCursor(),
          fill: getKnobFill(),
          height: getKnobDimensions(),
          keyline: {
            color: 'base',
            side: 'all',
            width: 1
          },
          radius: "circle",
          shadow: getKnobShadow(isHovering),
          width: getKnobDimensions()
        }));
      }));
    });
  };

  var className = props.className,
      description = props.description,
      disabled = props.disabled,
      id = props.id,
      invalid = props.invalid,
      label = props.label,
      size = props.size,
      value = props.value,
      wrap = props.wrap;
  var isChecked = value;
  var classes = classNames('Switch', {
    'Switch--isChecked': isChecked,
    'Switch--size--medium': size === 'medium',
    'Switch--size--small': size === 'small'
  }, className, {
    'Is--disabled': disabled
  });
  var idValue = id || generatedID.current;
  return /*#__PURE__*/React.createElement(FocusHandler, null, function (focusProps, inputMethod) {
    var isFocused = inputMethod !== null;
    return /*#__PURE__*/React.createElement(HoverHandler, null, function (hoverProps, isHovering) {
      return /*#__PURE__*/React.createElement(PressableControlLabel, _extends({}, focusProps, hoverProps, {
        className: classes,
        id: idValue,
        checked: isChecked,
        disabled: disabled,
        hovered: isHovering,
        invalid: invalid,
        wrap: wrap,
        target: renderSwitch(focusProps, hoverProps, idValue, isFocused, isHovering),
        label: label,
        description: description
      }));
    });
  });
};

Switch.defaultProps = {
  color: 'blue',
  disabled: false,
  invalid: false,
  onChange: _noop,
  size: 'medium',
  value: false,
  wrap: true
};
export default Switch;